import { ColumnExtended } from './ColumnExtended';
import { CheckmarkCircleFilled, ErrorCircleFilled, ArrowSyncFilled, WarningFilled, ProhibitedFilled, StackFilled, SendFilled, PersonFilled } from '@fluentui/react-icons';
import React from 'react';
import { CopyableId } from '../../components/CopyableId';


const DASHBOARD_MIN_COLUMN_WIDTH = 60;

export const DashboardColumns: ColumnExtended[] = [
    {
        key: 'documentNumber',
        name: 'Document Number',
        fieldName: 'documentId',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 1.5,
        onRender: (item) => <CopyableId value={item.documentId} />,
    },
    {
        key: 'documentType',
        name: 'Document Type',
        fieldName: 'documentType',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 1.2,
    },
    {
        key: 'country',
        name: 'Country',
        fieldName: 'countryCode',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH,
    },
    {
        key: 'company',
        name: 'Company Code',
        fieldName: 'companyCode',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH,
    },
    {
        key: 'partner',
        name: 'Calling Service',
        fieldName: 'eInvPartnerId',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 1.5,
        onRender: (item) => {
            if (item.scenario?.toLowerCase() === 'manual') {
                return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#5C2D91' }}><PersonFilled aria-hidden="true" /> Manual</span>;
            }
            return item.eInvPartnerId;
        }
    },
    {
        key: 'receivedTime',
        name: 'Received Time',
        fieldName: 'startEventTime',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 1.5,
        isSorted: true,
        isSortedDescending: true,
        isSortable: true

    },
    {
        key: 'completedTime',
        name: 'Completed Time',
        fieldName: 'endEventTime',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 1.5,
        isSortable: true
    },
    {
        key: 'status',
        name: 'Status',
        fieldName: 'status',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 1.8,
    onRender: (item) => {
            // Build the visual status badge (icon + colour + label) for the document status.
            let badge: React.ReactNode = null;
            if (item.status === 'Failed')
                badge = <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#A80000' }}><ErrorCircleFilled aria-hidden="true" /> Failed</span>;
            else if (item.status === 'Succeeded')
                badge = <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#0B6A0B' }}><CheckmarkCircleFilled aria-hidden="true" /> Succeeded</span>;
            else if (item.status === 'In Progress')
                badge = <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#CA5010' }}><ArrowSyncFilled aria-hidden="true" /> In Progress</span>;
            else if (item.status === 'Warning')
                badge = <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#6B5300' }}><WarningFilled aria-hidden="true" /> Warning</span>;
            else if (item.status === 'Cancel Succeeded')
                badge = <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#0B6A0B' }}><ProhibitedFilled aria-hidden="true" /> Cancel Succeeded</span>;
            else if (item.status === 'Cancel Failed')
                badge = <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#A80000' }}><ProhibitedFilled aria-hidden="true" /> Cancel Failed</span>;
            else if (item.status === 'Cancel In Progress')
                badge = <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#CA5010' }}><ProhibitedFilled aria-hidden="true" /> Cancel In Progress</span>;
            else if (item.status === 'Batched')
                badge = <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#5C2D91' }}><StackFilled aria-hidden="true" /> Batched</span>;
            else if (item.status === 'Sent')
                badge = <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#0E7090' }}><SendFilled aria-hidden="true" /> Sent</span>;

            if (badge === null) {
                return null;
            }

            // When the document is associated with a batch present in ACS, surface a link to the Batch
            // Dashboard regardless of the document status. If there is no batchId, render the plain status
            // badge without navigating to the Batch Dashboard.
            if (item.batchId) {
                return (
                    <a
                        href={`/batches?search=${encodeURIComponent(item.batchId)}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                        title={`View batch ${item.batchId}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {badge}
                    </a>
                );
            }

            return badge;
        }
    },
    {
        key: 'extReceiptId',
        name: 'Ext. Receipt Id',
        fieldName: 'extReceiptId',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 2.5,
    },
    {
        key: 'internalInvoiceNumber',
        name: 'Internal Invoice Number',
        fieldName: 'invoiceNumber',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 1.5,
    },
    {
        key: 'invoiceTotal',
        name: 'Amount',
        fieldName: 'invoiceAmount',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 2,
        onRender: (item) => {
            if (item.invoiceAmount != null && item.invoiceAmount !== '' && item.invoiceCurrency) {
                return `${item.invoiceAmount} ${item.invoiceCurrency}`;
            }
            return item.invoiceAmount != null && item.invoiceAmount !== '' ? String(item.invoiceAmount) : '';
        }
    },
    {
        key: 'supplier',
        name: 'Supplier',
        fieldName: 'supplier',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH,
    },
    {
        key: 'scenario',
        name: 'Scenario',
        fieldName: 'scenario',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH,
        isOptional: true,
    },
    {
        key: 'channel',
        name: 'Channel',
        fieldName: 'channel',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH
    },
    {
        key: 'salesMotion',
        name: 'Sales Motion',
        fieldName: 'salesMotion',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH,
        isOptional: true,
    },
    {
        key: 'errorCode',
        name: 'Error Code',
        fieldName: 'errorCode',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH,
    },
    {
        key: 'paymentType',
        name: 'Payment Type',
        fieldName: 'paymentType',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH,
        isOptional: true,
    },
    {
        key: 'companyName',
        name: 'Company Name',
        fieldName: 'companyName',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 2,
        isOptional: true,
        defaultHidden: true,
    },
    {
        key: 'taxId',
        name: 'Tax ID',
        fieldName: 'taxId',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 1.5,
        isOptional: true,
        defaultHidden: true,
    },
    {
        key: 'invoiceDueDate',
        name: 'Invoice Due Date',
        fieldName: 'invoiceDueDate',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH * 1.5,
        isOptional: true,
        defaultHidden: true,
    },
    {
        key: 'mode',
        name: 'Mode',
        fieldName: 'mode',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH,
        isOptional: true,
    },
    {
        key: 'numberOfSubmissions',
        name: 'Number of Submissions',
        fieldName: 'numberOfSubmissions',
        isResizable: true,
        minWidth: DASHBOARD_MIN_COLUMN_WIDTH,
        isOptional: true,
    }
]
